/**
 * Rung 2 of the fallback ladder — the DEVICE voice (SPEC_AUDIO_GUIDANCE §3.4 #2, SPEC_CAPTURE_UX_R2 §3.4 #2).
 *
 * `speechSynthesis` speaks the SAME text the screen shows, in a voice whose language matches the chosen one:
 * `hi-IN` first, then any `hi*`; `en-IN` first, then any `en*`. No match ⇒ `null`, and the caller shows the
 * rung-3 note — a device voice in the wrong language is worse than silence for a customer who cannot read the
 * screen, and an English voice reading Devanagari is not "Hindi guidance".
 *
 * Rate ≈ 0.9 (a touch under conversational) — the one prosody control that helps a first-time listener and is
 * portable across engines. Whether a cue may reach this rung at all is the caller's rule (`GuidancePlayer`).
 *
 * iPhone (WebKit — Safari AND Chrome on iOS) is strict about speech, and every rule below is one it enforces
 * (US-03482 G7, "Listen to this notice" was silent on an iPhone):
 *   · the FIRST utterance must start inside a user gesture — `primeSpeech()` speaks a silent one from the unlock tap,
 *     so later cues (auto-play on a step, the next notice section chained off `onend`) are allowed;
 *   · `cancel()` immediately followed by `speak()` can swallow the new utterance — we cancel only when something
 *     is actually speaking or queued, and never just to clear the silent primer;
 *   · `getVoices()` is often EMPTY until `voiceschanged` — an empty list is "not loaded yet", not "no voice": the
 *     utterance is spoken with `lang` alone and the platform picks its voice for that language;
 *   · an utterance with no live reference can be collected before it ends (its `onend` never fires) — the current
 *     one is held here.
 */

export const SPEECH_RATE = 0.9;

/**
 * The BCP-47 tag a deck locale speaks in: `en` / `hi` speak the Indian voices first. VC-12: Arabic (a pack-gated Gulf
 * language) asks for plain `ar` — `pickVoice` then takes any Arabic voice the device has (ar-SA, ar-AE, ar-001…),
 * never an India-region tag that no Arabic voice carries.
 */
export function bcp47For(locale: string): string {
  const base = (locale || 'en').toLowerCase().split('-')[0];
  return base === 'hi' ? 'hi-IN' : base === 'en' ? 'en-IN' : base === 'ar' ? 'ar' : `${base}-IN`;
}

type VoiceLike = { readonly lang?: string; readonly name?: string };

/**
 * Exact region match first (`hi-IN`), then the language (`hi`), in the order the platform listed them (a
 * platform's own order is its quality order). Underscores (`hi_IN`, seen on older Android WebViews) count.
 */
export function pickVoice<T extends VoiceLike>(voices: readonly T[], tag: string): T | null {
  const want = tag.toLowerCase().replace('_', '-');
  const base = want.split('-')[0] ?? want;
  const norm = (v: VoiceLike): string => (v.lang ?? '').toLowerCase().replace('_', '-');
  return voices.find((v) => norm(v) === want) ?? voices.find((v) => (norm(v).split('-')[0] ?? '') === base) ?? null;
}

export interface SpeakHandle {
  readonly voiceName: string;
  readonly tag: string;
  readonly stop: () => void;
}

export interface SpeakOptions {
  readonly synth?: SpeechSynthesis | null;
  readonly rate?: number;
  /**
   * Called ONCE when the whole text has been spoken, or with the platform's error code (`not-allowed`, `canceled`,
   * `interrupted`, `synthesis-failed`, …) when an utterance errored. Never called after the handle was stopped.
   */
  readonly onEnd?: (error?: string) => void;
}

/** Long sections are spoken as sentence chunks of about this many characters (WebKit stalls on long utterances). */
export const SPEECH_CHUNK_CHARS = 250;

/**
 * Split at sentence ends (., !, ?, the Devanagari danda) into chunks of ~`max` characters; a single sentence longer
 * than `max` is cut at the last space before the limit. Never returns an empty chunk.
 */
export function splitForSpeech(text: string, max = SPEECH_CHUNK_CHARS): readonly string[] {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean === '') return [];
  if (clean.length <= max) return [clean];
  const sentences = clean.match(/[^.!?\u0964]+[.!?\u0964]*\s*/g) ?? [clean];
  const out: string[] = [];
  let buf = '';
  const push = (piece: string): void => {
    const t = piece.trim();
    if (t !== '') out.push(t);
  };
  for (const raw of sentences) {
    let sentence = raw;
    while (sentence.trim().length > max) {
      const cut = sentence.lastIndexOf(' ', max);
      const at = cut > 0 ? cut : max;
      push(buf);
      buf = '';
      push(sentence.slice(0, at));
      sentence = sentence.slice(at);
    }
    if ((buf + sentence).trim().length > max) {
      push(buf);
      buf = sentence;
    } else {
      buf += sentence;
    }
  }
  push(buf);
  return out;
}

/** Every chunk of the read in flight, held so a collector cannot drop one (and its `onend`) mid-sentence. */
let held: SpeechSynthesisUtterance[] = [];
/** The last voice list the platform reported — iOS fills it asynchronously (`voiceschanged`). */
let knownVoices: readonly SpeechSynthesisVoice[] = [];
let warmed = false;
/** The silent priming utterance while it is still queued/speaking — never cancelled just to make room. */
let primer: SpeechSynthesisUtterance | null = null;
let primed = false;

type UtteranceCtor = new (t: string) => SpeechSynthesisUtterance;

function utteranceCtor(): UtteranceCtor | null {
  return (globalThis as unknown as { SpeechSynthesisUtterance?: UtteranceCtor }).SpeechSynthesisUtterance ?? null;
}

function synthesis(opts: SpeakOptions): SpeechSynthesis | null {
  if (opts.synth !== undefined) return opts.synth;
  const w = globalThis as unknown as { speechSynthesis?: SpeechSynthesis };
  return w.speechSynthesis ?? null;
}

/**
 * Call INSIDE a user gesture (the unlock tap). Speaks one silent utterance so iOS WebKit lets later speech start
 * outside a gesture, and asks for the voice list early (iOS fills it asynchronously). Idempotent; never throws.
 */
export function primeSpeech(opts: SpeakOptions = {}): void {
  if (primed) return;
  const synth = synthesis(opts);
  const Utterance = utteranceCtor();
  if (!synth || !Utterance || typeof synth.speak !== 'function') return;
  try {
    if (typeof synth.getVoices === 'function') synth.getVoices();
    const u = new Utterance(' ');
    u.volume = 0;
    const clear = (): void => {
      if (primer === u) primer = null;
    };
    u.onend = clear;
    u.onerror = clear;
    primer = u;
    synth.speak(u);
    primed = true;
  } catch {
    primer = null;
  }
}

/** True when this device can speak the tag — an empty (not yet loaded) voice list counts as "try". */
export function deviceCanSpeak(tag: string, opts: SpeakOptions = {}): boolean {
  const synth = synthesis(opts);
  if (!synth || typeof synth.getVoices !== 'function') return false;
  const voices = synth.getVoices() as unknown as readonly VoiceLike[];
  return voices.length === 0 || pickVoice(voices, tag) !== null;
}

/**
 * Ask for the voice list early and keep it current (US-03482 #32 review): call on the consent screen / app mount.
 * iOS returns an empty list until `voiceschanged`; the cached list is what `speak` falls back to. Idempotent.
 */
export function warmVoices(opts: SpeakOptions = {}): void {
  const synth = synthesis(opts);
  if (!synth || typeof synth.getVoices !== 'function') return;
  try {
    const now = synth.getVoices();
    if (now && now.length > 0) knownVoices = now;
    if (!warmed && typeof synth.addEventListener === 'function') {
      synth.addEventListener('voiceschanged', () => {
        const next = synth.getVoices();
        if (next && next.length > 0) knownVoices = next;
      });
      warmed = true;
    }
  } catch {
    /* no list now; `speak` speaks with the language tag alone */
  }
}

export function resetSpeechForTests(): void {
  held = [];
  primer = null;
  primed = false;
  knownVoices = [];
  warmed = false;
}

/**
 * Speak `text` in `tag`'s voice, as sentence chunks of ~`SPEECH_CHUNK_CHARS`, all queued at once (inside the
 * gesture when the caller is in one) and all held until they end. `null` when the platform has no
 * `speechSynthesis`, no `SpeechSynthesisUtterance`, or a LOADED voice list with no voice for the language — the
 * caller then takes rung 3. An EMPTY list (iOS before `voiceschanged`) falls back to the cached list, else speaks with
 * `lang` alone. Never throws at the call site.
 */
export function speak(text: string, tag: string, opts: SpeakOptions = {}): SpeakHandle | null {
  const synth = synthesis(opts);
  if (!synth || typeof synth.speak !== 'function' || typeof synth.getVoices !== 'function') return null;
  const Utterance = utteranceCtor();
  if (!Utterance) return null;
  const listed = (synth.getVoices() ?? []) as unknown as readonly (VoiceLike & SpeechSynthesisVoice)[];
  const voices = listed.length > 0 ? listed : (knownVoices as unknown as readonly (VoiceLike & SpeechSynthesisVoice)[]);
  const voice = pickVoice(voices, tag);
  if (!voice && voices.length > 0) return null;
  const chunks = splitForSpeech(text);
  if (chunks.length === 0) return null;
  try {
    // iOS: cancel() right before speak() can swallow the new utterance. Cancel only a read of OURS that is still
    // unfinished (never the primer, never on `synth.speaking`, which WebKit still reports inside the last `onend` —
    // exactly when the next notice section is chained).
    if (held.length > 0 && primer === null) {
      held = [];
      synth.cancel();
    }
    let live = true;
    let finished = false;
    const mine: SpeechSynthesisUtterance[] = [];
    const finish = (error?: string): void => {
      if (finished) return;
      finished = true;
      live = false;
      held = held.filter((u) => !mine.includes(u));
      opts.onEnd?.(error);
    };
    chunks.forEach((chunk, i) => {
      const u = new Utterance(chunk);
      if (voice) u.voice = voice as SpeechSynthesisVoice;
      u.lang = voice?.lang ?? tag;
      u.rate = opts.rate ?? SPEECH_RATE;
      u.onend = () => {
        if (i === chunks.length - 1) finish();
      };
      u.onerror = (e: Event) => {
        const code = (e as unknown as { error?: string } | null)?.error ?? 'synthesis-failed';
        finish(code);
      };
      mine.push(u);
    });
    held = [...held, ...mine];
    for (const u of mine) synth.speak(u);
    // Chrome/WebKit can leave the queue paused after a backgrounded tab; a paused queue speaks nothing.
    if (synth.paused && typeof synth.resume === 'function') synth.resume();
    return {
      voiceName: voice?.name ?? '',
      tag: voice?.lang ?? tag,
      stop: () => {
        // Cancel only a LIVE read: a handle whose read already ended must not cancel the next one (iOS swallow).
        if (!live) return;
        live = false;
        finished = true;
        held = held.filter((u) => !mine.includes(u));
        try {
          synth.cancel();
        } catch {
          /* a platform that refuses to cancel is not a reason to crash the screen */
        }
      },
    };
  } catch {
    return null;
  }
}
